/** What is in the graph, and why — one panel.
 *
 * The atlas had a "Categories" menu that hid node types, and nothing at all for
 * edges. That is half a control: it let you ask "what is in my graph" but never
 * "why are these things connected". The gap is not academic. A vault of 200
 * files across 12 folders draws a `co_located` edge for every pair inside every
 * folder — hundreds of lines asserting nothing more than "these sit in the same
 * directory" — and that metadata mesh buries the handful of links you drew
 * yourself. Until you can switch it off you cannot read your own graph.
 *
 * So this panel answers both questions in one place, and adds the third the
 * atlas never asked: how much of this does anyone actually believe? Inferred
 * links carry a confidence; the slider is the floor. Deterministic links carry
 * none and the slider leaves them alone, because a threshold on a thing with no
 * confidence is meaningless.
 *
 * The three provenance classes it names — authored, derived, inferred — are the
 * same ones `paint.ts` draws, from the same `edgeClass()`. One definition.
 */
import { Eye, EyeOff, Layers, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { useGraphOntology } from "@/hooks/queries";
import type { NodeType } from "@/lib/api";
import { EDGE_COLOR, NODE_COLOR, NODE_LABELS, humanizeEdgeType } from "@/lib/palette";
import { cn } from "@/lib/utils";
import { useGraph, type GLink } from "@/stores/graph";

import { edgeClass, type EdgeClass } from "./paint";

/** Node categories in reading order: what you made first, what was derived
 *  from it last. Types absent from the atlas are not listed. */
const TYPE_ORDER: NodeType[] = [
  "document",
  "org",
  "concept",
  "note",
  "ai_output",
  "section",
  "fragment",
];

/** How each provenance class reads, and how its swatch is drawn — the legend
 *  for the strokes on the canvas, stated in words rather than left to be
 *  reverse-engineered from the lines. */
const CLASS_COPY: Record<EdgeClass, { label: string; blurb: string }> = {
  authored: { label: "Authored", blurb: "You drew it. Solid, full weight." },
  derived: { label: "Derived", blurb: "Structure or folder layout implies it. Solid, thin." },
  inferred: { label: "Inferred", blurb: "A model proposed it. Dashed; fainter when less sure." },
};

const CLASS_ORDER: EdgeClass[] = ["authored", "derived", "inferred"];

/** A short stroke drawn the way the canvas draws that class, so the legend and
 *  the atlas cannot disagree about what a dashed line means. */
function ClassSwatch({ kind, color }: { kind: EdgeClass; color: string }) {
  return (
    <svg width="22" height="8" viewBox="0 0 22 8" aria-hidden className="shrink-0">
      <line
        x1="1"
        y1="4"
        x2="21"
        y2="4"
        stroke={color}
        strokeWidth={kind === "authored" ? 2.2 : kind === "derived" ? 1 : 1.4}
        strokeDasharray={kind === "inferred" ? "4 2.5" : undefined}
        strokeLinecap="round"
      />
    </svg>
  );
}

/** The toggle affordance.
 *
 * Every row here is a switch, and nothing said so: before the first click the
 * panel read as a legend with counts. An always-on icon on every row is
 * clutter for a control most people set once, so the eye appears on hover —
 * and stays on, crossed out, for anything currently hidden, where it is not
 * decoration but the state itself.
 */
function RowEye({ hidden }: { hidden: boolean }) {
  return hidden ? (
    <EyeOff className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" aria-hidden />
  ) : (
    <Eye
      className="h-3.5 w-3.5 shrink-0 text-primary/70 opacity-0 transition-opacity group-hover:opacity-100"
      aria-hidden
    />
  );
}

export function GraphLayers() {
  const version = useGraph((s) => s.version);
  const hiddenTypes = useGraph((s) => s.hiddenTypes);
  const hiddenEdgeTypes = useGraph((s) => s.hiddenEdgeTypes);
  const minConfidence = useGraph((s) => s.minConfidence);
  const ontology = useGraphOntology();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  // Close on click-away and on Escape. The panel sits over the canvas, where a
  // click is also a graph gesture, so leaving it open under the pointer would
  // eat selections.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", away);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("mousedown", away);
      window.removeEventListener("keydown", esc);
    };
  }, [open]);

  // Counts come off the store rather than the render data, so a category shows
  // its true size even while hidden — otherwise turning something off would
  // make its own row read zero.
  const { nodeRows, edgeRows, inferredTotal, inferredShown } = useMemo(() => {
    const { nodes, links } = useGraph.getState();
    const nodeCounts = new Map<NodeType, number>();
    for (const n of nodes.values()) nodeCounts.set(n.type, (nodeCounts.get(n.type) ?? 0) + 1);

    const edgeCounts = new Map<string, { n: number; cls: EdgeClass }>();
    let total = 0;
    let shown = 0;
    for (const l of links.values() as Iterable<GLink>) {
      const cls = edgeClass(l);
      const row = edgeCounts.get(l.edge.type);
      edgeCounts.set(l.edge.type, { n: (row?.n ?? 0) + 1, cls: row?.cls ?? cls });
      if (cls === "inferred") {
        total += 1;
        if ((l.edge.confidence ?? 0.35) >= minConfidence) shown += 1;
      }
    }
    return {
      nodeRows: TYPE_ORDER.filter((t) => nodeCounts.has(t)).map((t) => ({
        type: t,
        count: nodeCounts.get(t)!,
      })),
      edgeRows: [...edgeCounts.entries()]
        .sort((a, b) => b[1].n - a[1].n)
        .map(([type, v]) => ({ type, count: v.n, cls: v.cls })),
      inferredTotal: total,
      inferredShown: shown,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, minConfidence]);

  const phrase = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of ontology.data?.edge_types ?? []) m.set(r.type, r.phrase);
    return m;
  }, [ontology.data]);

  const hiddenCount = hiddenTypes.size + hiddenEdgeTypes.size + (minConfidence > 0 ? 1 : 0);
  if (!nodeRows.length) return null;

  return (
    <div className="relative" ref={boxRef}>
      <Tip label="Choose what the atlas draws — node categories, relationships, and how sure a guess must be">
        <Button
          size="sm"
          variant={hiddenCount > 0 ? "default" : "outline"}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="gv-graph-layers"
        >
          <Layers className="h-3.5 w-3.5" />
          Layers
          {hiddenCount > 0 && <span className="font-mono text-2xs">· {hiddenCount}</span>}
        </Button>
      </Tip>

      {open && (
        <div
          id="gv-graph-layers"
          className="absolute right-0 top-full z-30 mt-1.5 max-h-[min(70vh,520px)] w-[272px] overflow-y-auto rounded-md border border-border bg-popover/95 p-1 shadow-deep backdrop-blur animate-rise-in"
        >
          {/* --- nodes ------------------------------------------------ */}
          <p className="px-2 pb-1 pt-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            Nodes on the canvas
          </p>
          {nodeRows.map(({ type, count }) => {
            const hidden = hiddenTypes.has(type);
            return (
              <button
                key={type}
                onClick={() => useGraph.getState().toggleType(type)}
                aria-pressed={!hidden}
                title={hidden ? `Show ${NODE_LABELS[type]} nodes` : `Hide ${NODE_LABELS[type]} nodes`}
                className={cn(
                  "group flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] transition-colors hover:bg-accent",
                  hidden ? "text-muted-foreground/55" : "text-muted-foreground hover:text-foreground",
                )}
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ background: NODE_COLOR[type], opacity: hidden ? 0.3 : 1 }}
                />
                <span className={cn("capitalize", hidden && "line-through")}>
                  {NODE_LABELS[type]}
                </span>
                <span className="ml-auto font-mono text-[10px] text-muted-foreground/70">
                  {count}
                </span>
                <RowEye hidden={hidden} />
              </button>
            );
          })}

          {/* --- connections ------------------------------------------ */}
          {edgeRows.length > 0 && (
            <>
              <div className="mx-2 my-1 h-px bg-border" />
              <p className="px-2 pb-1 pt-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                Connections
              </p>
              {edgeRows.map(({ type, count, cls }) => {
                const hidden = hiddenEdgeTypes.has(type);
                const color = EDGE_COLOR[type] ?? "#333338";
                return (
                  <button
                    key={type}
                    onClick={() => useGraph.getState().toggleEdgeType(type)}
                    aria-pressed={!hidden}
                    title={
                      phrase.get(type)
                        ? `${hidden ? "Show" : "Hide"} — source ${phrase.get(type)} target`
                        : undefined
                    }
                    className={cn(
                      "group flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] transition-colors hover:bg-accent",
                      hidden ? "text-muted-foreground/55" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <span style={{ opacity: hidden ? 0.3 : 1 }}>
                      <ClassSwatch kind={cls} color={color} />
                    </span>
                    <span className={cn("truncate", hidden && "line-through")}>
                      {humanizeEdgeType(type)}
                    </span>
                    <span className="ml-auto font-mono text-[10px] text-muted-foreground/70">
                      {count}
                    </span>
                    <RowEye hidden={hidden} />
                  </button>
                );
              })}
            </>
          )}

          {/* --- confidence ------------------------------------------- */}
          {inferredTotal > 0 && (
            <>
              <div className="mx-2 my-1 h-px bg-border" />
              <div className="px-2 pb-2 pt-1">
                <label
                  htmlFor="gv-confidence"
                  className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground"
                >
                  Minimum confidence
                </label>
                <div className="mt-1.5 flex items-center gap-2">
                  <input
                    id="gv-confidence"
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={Math.round(minConfidence * 100)}
                    onChange={(e) =>
                      useGraph.getState().setMinConfidence(Number(e.target.value) / 100)
                    }
                    className="h-1 flex-1 accent-primary"
                  />
                  <span className="w-9 text-right font-mono text-[10px] text-muted-foreground">
                    {Math.round(minConfidence * 100)}%
                  </span>
                </div>
                <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground/80">
                  {inferredShown} of {inferredTotal} inferred links shown. Authored and
                  derived links carry no confidence and are never filtered here.
                </p>
              </div>
            </>
          )}

          {/* --- the legend, and the way back -------------------------- */}
          <div className="mx-2 my-1 h-px bg-border" />
          <div className="px-2 pb-1 pt-1">
            {CLASS_ORDER.map((k) => (
              <div key={k} className="flex items-start gap-2 py-0.5">
                <span className="mt-1.5">
                  <ClassSwatch kind={k} color="#8a8a94" />
                </span>
                <span className="text-[11px] leading-relaxed text-muted-foreground">
                  <span className="text-foreground/85">{CLASS_COPY[k].label}</span>
                  {" — "}
                  {CLASS_COPY[k].blurb}
                </span>
              </div>
            ))}
          </div>

          {hiddenCount > 0 && (
            <button
              onClick={() => useGraph.getState().resetGraphFilters()}
              className="mt-0.5 flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-[12px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Show everything
            </button>
          )}
        </div>
      )}
    </div>
  );
}
